/**
 * Patient Filters API
 * RTK Query API for patient filter metadata and values
 */

import { api } from '@client/app/api';
import type { FilterMetadataResponse, FilterValuesResponse } from '../types/filter-metadata.types';

export const patientFiltersApi = api.injectEndpoints({
    endpoints: builder => ({
        /**
         * Get filter metadata for patients
         */
        getFilterMetadata: builder.query<FilterMetadataResponse, void>({
            query: () => '/patients/filters/metadata',
            providesTags: ['Patients'],
        }),

        /**
         * Get values for a specific filter field
         */
        getFilterValues: builder.query<FilterValuesResponse, { field: string; search?: string; limit?: number }>({
            query: ({ field, search, limit }) => ({
                url: `/patients/filters/${field}/values`,
                params: { search, limit },
            }),
            providesTags: (_result, _error, { field }) => [{ type: 'Patients' as const, id: `filter-${field}` }],
        }),

        /**
         * Get gender filter values
         */
        getGenderValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/gender/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-gender' }],
        }),

        /**
         * Get race filter values
         */
        getRaceValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/race/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-race' }],
        }),

        /**
         * Get ethnicity filter values
         */
        getEthnicityValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/ethnicity/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-ethnicity' }],
        }),

        /**
         * Get diagnosis filter values
         */
        getDiagnosisValues: builder.query<FilterValuesResponse, { search?: string; limit?: number } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/diagnosis/values',
                params: { search: params?.search, limit: params?.limit },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-diagnosis' }],
        }),

        /**
         * Get note type filter values
         */
        getNoteTypeValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/note-type/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-note-type' }],
        }),

        /**
         * Get medication name filter values
         */
        getMedicationNameValues: builder.query<FilterValuesResponse, { search?: string; limit?: number } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/medication-name/values',
                params: { search: params?.search, limit: params?.limit },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-medication-name' }],
        }),

        /**
         * Get medication class filter values
         */
        getMedicationClassValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/medication-class/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-medication-class' }],
        }),

        /**
         * Get procedure type filter values
         */
        getProcedureTypeValues: builder.query<FilterValuesResponse, { search?: string; limit?: number } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/procedure-type/values',
                params: { search: params?.search, limit: params?.limit },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-procedure-type' }],
        }),

        /**
         * Get condition type filter values
         */
        getConditionTypeValues: builder.query<FilterValuesResponse, { search?: string } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/condition-type/values',
                params: { search: params?.search },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-condition-type' }],
        }),

        /**
         * Get measurement type filter values
         */
        getMeasurementTypeValues: builder.query<FilterValuesResponse, { search?: string; limit?: number } | void>({
            query: (params = {}) => ({
                url: '/patients/filters/measurement-type/values',
                params: { search: params?.search, limit: params?.limit },
            }),
            providesTags: [{ type: 'Patients' as const, id: 'filter-measurement-type' }],
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetFilterMetadataQuery,
    useGetFilterValuesQuery,
    useGetGenderValuesQuery,
    useGetRaceValuesQuery,
    useGetEthnicityValuesQuery,
    useGetDiagnosisValuesQuery,
    useGetNoteTypeValuesQuery,
    useGetMedicationNameValuesQuery,
    useGetMedicationClassValuesQuery,
    useGetProcedureTypeValuesQuery,
    useGetConditionTypeValuesQuery,
    useGetMeasurementTypeValuesQuery,
} = patientFiltersApi;
